import type { AI_SUGGESTION_TRIGGERED_FROM } from '@atlaskit/editor-common/analytics/types/ai-inline-suggestion-events';
import type { Command } from '@atlaskit/editor-common/types/command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import type { DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import {
	clearSuggestionWithAnalytics,
	insertInlineSuggestionWithAnalytics,
	fireNewSuggestionAnalytics,
} from './commands/commands-with-analytics';
import { SuggestionManager } from './handlers/suggestion-manager';
import { getPluginState as getInlineSuggestionState } from './plugin-factory';
import type { InlineSuggestion } from './states';

export function docHasChanged(prevState: EditorState, newState: EditorState): boolean {
	return !prevState.doc.eq(newState.doc);
}

export function localeHasChanged(state: EditorState, currentLocale: string): boolean {
	return getInlineSuggestionState(state).locale !== currentLocale;
}

export enum KeydownActionType {
	INSERT = 'INSERT',
	DISMISS = 'DISMISS',
}

export const createKeyHander = (
	actionType: KeydownActionType,
	triggeredFrom: AI_SUGGESTION_TRIGGERED_FROM,
	api?: ExtractInjectionAPI<AIPlugin>,
): Command => {
	return (state, dispatch) => {
		if (!state || !getInlineSuggestionState(state)) {
			return false;
		}

		if (getInlineSuggestionState(state)?.inlineSuggestion) {
			if (actionType === KeydownActionType.INSERT) {
				insertInlineSuggestionWithAnalytics(api?.analytics?.actions)(triggeredFrom)(
					state,
					dispatch,
				);
				return true;
			}

			if (actionType === KeydownActionType.DISMISS) {
				clearSuggestionWithAnalytics(api?.analytics?.actions)(triggeredFrom)(state, dispatch);
				return true;
			}
		}
		return false;
	};
};

export const fireAnalyticsForNewInlineSuggestionShown = (
	oldState: EditorState,
	newState: EditorState,
	api?: ExtractInjectionAPI<AIPlugin>,
) => {
	const { inlineSuggestion: oldInlineSuggestions } = getInlineSuggestionState(oldState);
	const { inlineSuggestion: newInlineSuggestions } = getInlineSuggestionState(newState);

	if (
		oldInlineSuggestions?.metadata !== newInlineSuggestions?.metadata &&
		newInlineSuggestions !== null
	) {
		fireNewSuggestionAnalytics({
			analyticsApi: api?.analytics?.actions,
			state: newState,
		});
	}
	return null;
};

export function isFocusTransitioningToPopup(event: FocusEvent): boolean {
	const relatedTarget = event.relatedTarget || document.activeElement;
	return (
		relatedTarget instanceof Element && Boolean(relatedTarget.closest('[data-editor-popup="true"]'))
	);
}

export function hasActiveSuggestion(state: EditorState): boolean {
	const { inlineSuggestion } = getInlineSuggestionState(state);
	return Boolean(inlineSuggestion);
}

function isSelectionAtEndOfDocument(state: EditorState) {
	return state.selection.from === state.doc.nodeSize - 3;
}

function hasActiveCompletionHintDecoration(decorationSet: DecorationSet): boolean {
	return decorationSet
		.find()
		.some((deco) => deco?.spec?.type === SuggestionManager.DECORATION_TYPES.completionHint);
}

function isAtCompletionHintBoundary(
	selection: Selection,
	inlineSuggestion?: InlineSuggestion | null,
): boolean {
	return selection.from === inlineSuggestion?.to;
}

export function shouldInterceptRightArrowAtDocEnd(state: EditorState): boolean {
	const { selection } = state;
	if (!selection.empty || !isSelectionAtEndOfDocument(state)) {
		return false;
	}
	const { inlineSuggestion, decorationSet } = getInlineSuggestionState(state);
	const hintActive = hasActiveCompletionHintDecoration(decorationSet);
	return hintActive && isAtCompletionHintBoundary(selection, inlineSuggestion);
}
